/**
 * Shared boolean-copy helpers for the data objects in `src/model`.
 *
 * Every model class copies a server payload onto itself with a generic
 * loop. Those loops are value-driven and get two things wrong for a flag:
 *
 * 1. **`false` is dropped.** The truthiness-based loops (`if (obj[prop])`,
 *    `if (obj['someFlag'])`) skip `false` entirely, so a row the server
 *    says is `false` keeps the client-side constructor default of `true`.
 *    That is silent *and* self-inconsistent — a class whose `update()`
 *    uses the `typeof`-based loop copies `false` correctly, so the same
 *    row reads one way on first load and the other way after the next
 *    live update.
 *
 * 2. **MySQL `TINYINT(1)` stays a number.** The column reaches some
 *    callers as `0` / `1` rather than a JSON boolean. The `typeof`-based
 *    loop happily parks that number in a boolean-typed field, and
 *    `[value]="true"` on a radio button does not match `1`, nor does
 *    `flag === false` match `0`.
 *
 * Both problems are fixed the same way: after the generic loop has run,
 * re-apply the known boolean columns through {@link toBoolean}.
 *
 * ## Scope: declared-boolean properties ONLY
 *
 * These helpers are driven by an explicit, hand-maintained allow-list
 * per class -- the names of the properties DECLARED `boolean` in that
 * class. They must never be driven by "does this value look coercible",
 * because this codebase is full of legacy columns that are functionally
 * boolean but genuinely `string` on the wire (`'TRUE'` / `'FALSE'` --
 * see {@link toBoolean}). Adding such a column to a `BOOLEAN_PROPERTIES`
 * list would corrupt it on the next save.
 *
 * Rule of thumb when extending a list: if the property's TypeScript
 * declaration is not literally `: boolean`, it does not belong there.
 *
 * Introduced for `EcommerceConnection` (see the class comment there) and
 * generalized so every data object with a boolean property gets the same
 * treatment.
 */

/**
 * Coerce a wire value to a real boolean.
 *
 * ONLY `boolean` and `number` are decoded. `number` is here for MySQL
 * `TINYINT(1)`, which reaches some callers as `0` / `1`.
 *
 * **Strings are deliberately NOT decoded.** This codebase has a large
 * family of legacy columns that are *functionally* boolean but are
 * genuinely `string` on both sides of the wire, carrying the literal
 * values `'TRUE'` / `'FALSE'` -- `Book.archived`, `Book.isProof`,
 * `Product.archived`, `Product.isProof`, `User.isAdmin`, `User.active`,
 * `DraftBody.useICC`, `Organization.hasActiveTab`,
 * `Organization.canCheckOutWithInvoicePayment` and many more. They are
 * compared with `=== 'TRUE'` throughout the app and are round-tripped
 * back to the server as strings; turning one into a real boolean would
 * be written back as `true` and silently misread by the legacy server.
 *
 * Refusing strings also protects ordinary text columns: a `Book.title`
 * of `'true'` or `'FALSE'` must stay exactly that string.
 *
 * Returns `undefined` when the value does not express a boolean.
 * `undefined` is NOT the same as `false` and must leave the existing
 * value alone, so a partial update cannot reset a flag to its default.
 */
export function toBoolean(value: any): boolean | undefined {
  if ( typeof value === 'boolean' ) {
    return value;
  }
  if ( typeof value === 'number' ) {
    return value !== 0;
  }
  return undefined;
}

/**
 * Apply the boolean columns in `properties` from `obj` onto `target`,
 * preserving `false` and normalizing `TINYINT(1)` style values.
 *
 * A property the payload does not carry at all is left alone, so a
 * partial update cannot reset a flag to its constructor default. A
 * property the payload carries but that does not express a boolean is
 * likewise left alone -- in particular a STRING value is always left
 * alone, so a legacy `'TRUE'` / `'FALSE'` column can never be rewritten
 * as a real boolean. See {@link toBoolean}.
 *
 * `properties` MUST list only names declared `: boolean` on `target`.
 *
 * @param target     the model instance being populated
 * @param obj        the raw payload being copied from
 * @param properties the names of the boolean columns on `target`
 */
export function copyBooleanProperties(target: any, obj: any, properties: string[]): void {
  if ( !target || !obj || !properties ) {
    return;
  }
  for ( const prop of properties ) {
    if ( Object.prototype.hasOwnProperty.call(obj, prop) ) {
      const value = toBoolean(obj[prop]);
      if ( value !== undefined ) {
        target[prop] = value;
      }
    }
  }
}




